import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    contactUrl: { type: String, default: "/contact/" },
    servicesUrl: { type: String, default: "/services/" },
    einvoicingUrl: { type: String, default: "/gems/einvoicing/" }
  };

  connect() {
    if (!navigator.modelContext?.provideContext) return;

    navigator.modelContext.provideContext({
      tools: [
        {
          name: "request_quote",
          description:
            "Submit a project request to SXN Labs. Opens the contact page with the user's message pre-filled. Use when a user wants a quote, pricing, or to describe a digitalization need.",
          inputSchema: {
            type: "object",
            properties: {
              subject: {
                type: "string",
                description: "Short subject line summarizing the request"
              },
              message: {
                type: "string",
                description: "Detailed description of the project or need"
              },
              email: {
                type: "string",
                description: "Email of the requester (optional)"
              }
            },
            required: ["message"]
          },
          execute: async ({ subject, message, email }) => {
            const params = new URLSearchParams();
            if (subject) params.set("subject", subject);
            if (message) params.set("message", message);
            if (email) params.set("email", email);
            const target = `${this.contactUrlValue}?${params.toString()}`;
            window.location.assign(target);
            return { ok: true, navigated_to: target };
          }
        },
        {
          name: "search_services",
          description:
            "Search SXN Labs services for a keyword. Returns matching service titles and short descriptions extracted from the services page.",
          inputSchema: {
            type: "object",
            properties: {
              query: { type: "string", description: "Keyword to match" }
            },
            required: ["query"]
          },
          execute: async ({ query }) => {
            const services = await this.#fetchServices();
            const q = (query || "").toLowerCase();
            const matches = services.filter(
              (s) =>
                s.title.toLowerCase().includes(q) ||
                s.description.toLowerCase().includes(q)
            );
            return { count: matches.length, results: matches };
          }
        },
        {
          name: "get_einvoicing_info",
          description:
            "Return key facts about the SXN Labs `einvoicing` Ruby gem: French B2B e-invoicing reform (September 2026), supported formats (Factur-X, UBL 2.1 Peppol BIS Billing 3.0, CII D16B), and useful links.",
          inputSchema: { type: "object", properties: {} },
          execute: async () => ({
            gem: "einvoicing",
            language: "Ruby",
            standard: "EN 16931",
            formats: ["Factur-X (PDF/A-3 + CII D16B)", "UBL 2.1 Peppol BIS Billing 3.0"],
            french_reform: "Mandatory B2B e-invoicing in France from September 2026",
            transmission: "PPF (Portail Public de Facturation) or PDP",
            links: {
              page: new URL(this.einvoicingUrlValue, window.location.origin).toString(),
              github: "https://github.com/sxnlabs/einvoicing",
              rubygems: "https://rubygems.org/gems/einvoicing"
            }
          })
        }
      ]
    });
  }

  async #fetchServices() {
    if (this.constructor._servicesCache) return this.constructor._servicesCache;
    try {
      const res = await fetch(this.servicesUrlValue, { headers: { Accept: "text/html" } });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const items = [...doc.querySelectorAll("main h2, main h3")].map((h) => {
        const next = h.nextElementSibling;
        return {
          title: h.textContent.trim(),
          description: next?.textContent.trim().slice(0, 280) || ""
        };
      });
      this.constructor._servicesCache = items;
      return items;
    } catch {
      return [];
    }
  }
}
